// The backend's view of the catalogue.
//
// A consumer of `locales/`, not a second copy of it. The strings live there so
// the server-rendered pages and this app read one file — two catalogues that
// drift is the failure mode the shape exists to prevent, and it is the kind
// that shows up as one surface saying "Merken" while the other says "Save".
//
// What stays here is what the backend needs and the server does not: the
// weekday value/label pairs the selects are built from, and the grammar that
// turns a recurrence rule into a sentence.

import de from '../locales/de.js';
import en from '../locales/en.js';

const CATALOGUES = { de, en };

// The server already resolved this — cookie, then Accept-Language, then German
// — and wrote the answer into the shell's `lang`. Reading it back is the whole
// mechanism: re-deriving it here produced a nav saying "Orte" above a heading
// saying "Venues", because the browser asks for English and the cookie said
// nothing. One question gets one resolution, in the place CLAUDE.md names.
//
// Guarded against there being no browser at all. The grammar below is worth
// unit-testing, and a module that throws on import cannot be — this file is
// the one place in app/ that gets imported by something other than the app.
function pick() {
  const declared = typeof document === 'undefined' ? '' : document.documentElement?.lang ?? '';
  const base = String(declared).toLowerCase().split('-')[0];
  return base === 'de' || base === 'en' ? base : 'de';
}

export const locale = pick();
export const t = CATALOGUES[locale] ?? de;

// Colour names a venue reads, not hex values. The list itself is
// server/lib/theme.js — this is only what the eight are called, and the two
// must stay in step or the picker offers something the server drops.
export const ACCENT_NAMES = [
  'violet', 'vermilion', 'ochre', 'olive', 'green', 'petrol', 'blue', 'magenta',
];

// Not translated, and not an oversight: these are colour names in a picker a
// venue uses once. Translating "Zinnober" to "Vermilion" would make two people
// looking at the same swatch call it different things.
export const ACCENT_LABELS = {
  violet: 'Violett', vermilion: 'Zinnober', ochre: 'Ocker', olive: 'Oliv',
  green: 'Grün', petrol: 'Petrol', blue: 'Blau', magenta: 'Magenta',
};

// --------------------------------------------------------------- dates

const TAG = locale === 'en' ? 'en-GB' : 'de-DE';

const format = (options) => new Intl.DateTimeFormat(TAG, { ...options, timeZone: 'UTC' });

const MONTH_YEAR = format({ month: 'long', year: 'numeric' });
const SHORT_DATE = format({ day: 'numeric', month: 'numeric', year: 'numeric' });
const LONG_DATE = format({ day: 'numeric', month: 'long', year: 'numeric' });
const MOMENT = format({ day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const WEEKDAY = format({ weekday: 'long' });
const WEEKDAY_ABBR = format({ weekday: 'short' });

function atUtc(iso) {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function monthLabel(year, month) {
  return MONTH_YEAR.format(new Date(Date.UTC(year, month - 1, 1)));
}

export function formatDate(iso) {
  const date = atUtc(iso);
  return date ? SHORT_DATE.format(date) : String(iso);
}

// A moment, not a day. formatDate takes a local date and appends midnight to
// it, so handing it a full timestamp produces an invalid Date and it falls back
// to printing the raw ISO string — which is what the health panel did. A job run
// and a mail send are instants and want the time shown with them.
export function formatMoment(iso) {
  const date = new Date(String(iso));
  return Number.isNaN(date.getTime()) ? String(iso) : MOMENT.format(date);
}

// Written out, not "2026-09-15": a date read aloud digit by digit is the kind
// of thing that makes a screen reader user give up on a calendar.
export function longDate(iso) {
  const date = atUtc(iso);
  return date ? LONG_DATE.format(date) : String(iso);
}

// Monday first. The German week starts there, and so does a club week — a
// programme is planned Monday to Sunday, not Sunday to Saturday. The names
// come from Intl rather than a list, so the English build says Mon where the
// German one says Mo without a second table to keep in step.
//
// 2024-01-01 is a Monday; the seven days from it are the week in order.
export const WEEKDAY_SHORT = Array.from({ length: 7 }, (_, i) =>
  WEEKDAY_ABBR.format(new Date(Date.UTC(2024, 0, 1 + i))));

// The rule model speaks two-letter codes; a select needs a name. Index 0 is
// Monday, matching WEEKDAY_SHORT above.
const CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

const WEEKDAY_NAMES = Object.fromEntries(CODES.map((code, i) =>
  [code, WEEKDAY.format(new Date(Date.UTC(2024, 0, 1 + i)))]));

export const WEEKDAY_VALUES = CODES.map((code) => [code, WEEKDAY_NAMES[code]]);

// ------------------------------------------------------------- plurals

const PLURAL = new Intl.PluralRules(TAG);

export function plural(count, one, many) {
  return `${count} ${PLURAL.select(count) === 'one' ? one : many}`;
}

const COUNTED = {
  de: {
    upcoming: ['kommender Termin', 'kommende Termine'],
    cancelled: ['abgesagter Termin', 'abgesagte Termine'],
    series: ['Reihe', 'Reihen'],
    venue: ['Ort', 'Orte'],
  },
  en: {
    upcoming: ['upcoming date', 'upcoming dates'],
    // "series" is its own plural, which is exactly why these pairs are data
    // and not a rule that appends an s.
    cancelled: ['cancelled date', 'cancelled dates'],
    series: ['series', 'series'],
    venue: ['venue', 'venues'],
  },
};

// Same shape as describeRecurrence: the app's language by default, explicit
// when somebody needs to assert one.
const counted = (kind) => (count, forLocale = locale) => {
  const key = forLocale === 'en' ? 'en' : 'de';
  const rules = new Intl.PluralRules(key === 'en' ? 'en-GB' : 'de-DE');
  const [one, many] = COUNTED[key][kind];
  return `${count} ${rules.select(count) === 'one' ? one : many}`;
};

export const upcomingLabel = counted('upcoming');
export const cancelledLabel = counted('cancelled');
export const seriesLabel = counted('series');
export const venueLabel = counted('venue');

// ---------------------------------------------------------- recurrence

// The four rule shapes the product supports, as plain language. Anything the
// rule model cannot express is not describable here either, which is the
// point — the wording and the model stay in step.
//
// Two grammars, not one with substitutions: German inflects the weekday
// ("jeden ersten Freitag im Monat", "jede 2. Woche mittwochs") where English
// does not, and a shared template with placeholders would have to encode that
// difference somewhere anyway. Both read the same rule object, which is what
// keeps the wording from forking the rule logic.
const GRAMMAR = {
  de: {
    once: 'einmalig',
    weekly: 'wöchentlich',
    monthly: 'monatlich',
    // Adverbial form, for when the weekday is not the head of the phrase.
    adverbs: {
      MO: 'montags', TU: 'dienstags', WE: 'mittwochs', TH: 'donnerstags',
      FR: 'freitags', SA: 'samstags', SU: 'sonntags',
    },
    setpos: { 1: 'ersten', 2: 'zweiten', 3: 'dritten', 4: 'vierten', '-1': 'letzten' },
    everyWeekday: (names) => `jeden ${names}`,
    everyNthWeek: (n, adverbs) => `jede ${n}. Woche ${adverbs}`,
    everyMonthDay: (day) => `jeden ${day}. im Monat`,
    everyNthWeekday: (position, names) => `jeden ${position} ${names} im Monat`,
    until: (date) => `bis ${date}`,
    join: (names) => (names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} und ${names.at(-1)}`),
  },
  en: {
    once: 'once',
    weekly: 'weekly',
    monthly: 'monthly',
    adverbs: {
      MO: 'Mondays', TU: 'Tuesdays', WE: 'Wednesdays', TH: 'Thursdays',
      FR: 'Fridays', SA: 'Saturdays', SU: 'Sundays',
    },
    setpos: { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', '-1': 'last' },
    everyWeekday: (names) => `every ${names}`,
    everyNthWeek: (n, adverbs) => `every ${ordinal(n)} week on ${adverbs}`,
    everyMonthDay: (day) => `on the ${ordinal(day)} of every month`,
    everyNthWeekday: (position, names) => `every ${position} ${names} of the month`,
    until: (date) => `until ${date}`,
    join: (names) => (names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`),
  },
};

// 1st, 2nd, 3rd, 4th — through Intl.PluralRules in ordinal mode rather than a
// hand-written table, because the exceptions are not where they look.
const ORDINAL = new Intl.PluralRules('en-GB', { type: 'ordinal' });
const SUFFIX = { one: 'st', two: 'nd', few: 'rd', other: 'th' };

function ordinal(n) {
  return `${n}${SUFFIX[ORDINAL.select(n)]}`;
}

// The weekday name in the *described* language, which is not always the one
// the app is running in: a test asks for German wording on an English machine,
// and getting the language from ambient globals is exactly what made that
// impossible to assert.
const NAMES = {
  de: nameWeekdays('de-DE'),
  en: nameWeekdays('en-GB'),
};

function nameWeekdays(tag) {
  const f = new Intl.DateTimeFormat(tag, { weekday: 'long', timeZone: 'UTC' });
  return Object.fromEntries(CODES.map((code, i) =>
    [code, f.format(new Date(Date.UTC(2024, 0, 1 + i)))]));
}

// `forLocale` defaults to the app's own, so every call site in the backend
// stays a one-argument call — but the signature says out loud that the wording
// depends on a language, which is the thing that makes it testable.
export function describeRecurrence(rule, forLocale = locale) {
  const key = forLocale === 'en' ? 'en' : 'de';
  const g = GRAMMAR[key];
  const names = NAMES[key];

  if (!rule || rule.freq === 'once') return g.once;

  const days = (rule.byday ?? []).filter((day) => names[day]);
  const interval = rule.interval ?? 1;

  if (rule.freq === 'weekly') {
    if (days.length === 0) return g.weekly;
    if (interval === 1) return g.everyWeekday(g.join(days.map((day) => names[day])));
    return g.everyNthWeek(interval, g.join(days.map((day) => g.adverbs[day])));
  }

  if (rule.freq === 'monthly') {
    if (rule.bymonthday) return g.everyMonthDay(rule.bymonthday);
    if (days.length > 0 && rule.bysetpos) {
      const position = g.setpos[String(rule.bysetpos)] ?? `${rule.bysetpos}.`;
      return g.everyNthWeekday(position, g.join(days.map((day) => names[day])));
    }
    return g.monthly;
  }

  return '';
}

export function describeUntil(rule, forLocale = locale) {
  if (!rule?.until) return null;
  const key = forLocale === 'en' ? 'en' : 'de';
  const date = new Intl.DateTimeFormat(key === 'en' ? 'en-GB' : 'de-DE', {
    day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${rule.until}T00:00:00Z`));
  return GRAMMAR[key].until(date);
}
